import React, { useCallback, useMemo } from 'react';
import { Grid, useBreakpoint } from '@printdeal/ui-library';
import { InView } from 'react-intersection-observer';
import cx from 'clsx';
import { ListProps, ProductListProps } from './types';
import ProductListCard from './ProductListCard';

export const ProductList = ({
  list = [],
  maxItems = 12,
  columns = 4,
  className = '',
  clickHandler = () => { },
  impressionHandler = () => { },
  cardVariant,
  twoColumnBreakpoint = 'max-lg',
  imagePriority = false,
}: ProductListProps) => {
  const isTwoColumnBreakpoint = useBreakpoint(twoColumnBreakpoint);
  const columnsPerRow = isTwoColumnBreakpoint ? 2 : columns;

  // We're breaking up the list onto several rows so that we can track impressions
  // for each row in a single call
  // Converting this: [product1, product2, product3, product4, ...]
  // to this:         [[product1, product2, product3], [product4, ...]]
  const productsList = useMemo(() => {
    const result: ListProps[][] = [];
    for (let i = 0; i < Math.min(list.length, maxItems); i += columnsPerRow) {
      result.push(
        list.slice(i, i + columnsPerRow).map((item, itemIndex) => ({
          ...item,
          itemIndex: i + itemIndex + 1,
        })),
      );
    }
    return result;
  }, [list, maxItems, columnsPerRow]);

  // When a row of products comes into view, we want to track impressions for all products in that row
  const observerCallbackOnProducts = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry, rowIndex: number) => {
      if (inView && entry.isIntersecting) {
        // We grab all the items from the row that is in view
        const products = productsList[rowIndex].map((product) => ({
          itemName: product.name || '',
          itemId: product.id,
          itemCategory: product.productCategory?.categoryName || '',
          itemIndex: product.itemIndex,
        }));
        impressionHandler({ products });
      }
    }, [impressionHandler, productsList],
  );

  const columnClass = columns === 3 ? 'md:pd-grid-cols-3' : 'lg:pd-grid-cols-4';

  if (list.length === 0) {
    return null;
  }

  return (
    <div className={cx(className, 'pd-grid pd-gap-6')}>
      {productsList.map((productList: ListProps[], rowIndex) => (
        <InView
          onChange={(inView, entry) => observerCallbackOnProducts(inView, entry, rowIndex)}
          key={`product-list-row-${productList[0].id}`}
          threshold={1}
          triggerOnce
        >
          {({ ref }) => (
            <Grid
              ref={ref}
              className={cx(
                columnClass,
                'pd-grid-cols-2 pd-gap-6',
              )}
            >
              {productList.map((item) => (
                <Grid.Item key={item.name} onClick={() => clickHandler(item.itemIndex, item.name || '')}>
                  {item.featuredImage && item.path ? (
                    <ProductListCard product={item} imagePriority={imagePriority} cardVariant={cardVariant} />
                  ) : null}
                </Grid.Item>
              ))}
            </Grid>
          )}
        </InView>
      ))}
    </div>
  );
};
